import { useState, FormEvent } from 'react';

export default function ContactFormSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: '',
    transactionalConsent: false,
    marketingConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('transactionalConsent', formData.transactionalConsent ? 'Yes' : 'No');
      formDataToSend.append('marketingConsent', formData.marketingConsent ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d5j359n9u36dslcgh4og', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          message: '',
          transactionalConsent: false,
          marketingConsent: false,
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-form-section" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left - Contact Info */}
          <div>
            <div className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-6">
              Get In Touch
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Ready to <strong>Sell Your Home, land, or Apartments?</strong>
            </h2>
            
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Contact us today for a free, no-obligation cash offer. We'll respond within 24 hours and can close on your timeline.
            </p>

            {/* Contact Methods */}
            <div className="space-y-6 mb-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-phone-fill text-teal-600 text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Call Us</div>
                  <a href="tel:+1-915-206-5814" className="text-lg text-teal-600 hover:text-teal-700 font-bold cursor-pointer">
                    (915) 206-5814
                  </a>
                  <p className="text-sm text-gray-600 mt-1">Monday - Friday: 8am - 7pm<br />Saturday: 9am - 5pm</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-mail-fill text-teal-600 text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Email Us</div>
                  <a href="mailto:ElPaso@trexrealestate.com" className="text-lg text-teal-600 hover:text-teal-700 cursor-pointer">
                    ElPaso@trexrealestate.com
                  </a>
                  <p className="text-sm text-gray-600 mt-1">We respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-fill text-teal-600 text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Visit Us</div>
                  <p className="text-gray-700">
                    3016 Gull Lake Pl<br />
                    El Paso, TX 79936
                  </p>
                </div>
              </div>
            </div>

            {/* Process Steps */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-6">How It Works</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Contact Us</div>
                    <p className="text-sm text-gray-600">Fill out the form or give us a call</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Get Your Offer</div>
                    <p className="text-sm text-gray-600">Receive a fair cash offer within 24 hours</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Close Fast</div>
                    <p className="text-sm text-gray-600">Close in as little as 7 days and get paid</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Get my cash offer now</h3>
            
            <form id="cash-offer-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="offer-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="offer-name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="offer-email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="offer-email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="offer-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="offer-phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(915) 555-0100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="offer-address" className="block text-sm font-semibold text-gray-700 mb-2">
                  Property Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="offer-address"
                  name="address"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street, El Paso, TX 79901"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="offer-message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Tell Us About Your Situation
                </label>
                <textarea
                  id="offer-message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us about your property and why you're looking to sell..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="offer-transactionalConsent"
                    name="transactionalConsent"
                    required
                    checked={formData.transactionalConsent}
                    onChange={(e) => setFormData({ ...formData, transactionalConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="offer-transactionalConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                    <span className="text-red-500">*</span> I consent to receive transactional messages from TREX RE LLC to the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                  </label>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="offer-marketingConsent"
                    name="marketingConsent"
                    checked={formData.marketingConsent}
                    onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                    className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="offer-marketingConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                    I consent to receive marketing and promotional messages from TREX RE LLC at the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                  Thank you! We've received your message and will contact you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  Something went wrong. Please try again or call us directly at (915) 206-5814.
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                <i className="ri-lock-fill"></i> Your information is 100% secure and will never be shared
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
